import { ref, readonly, onScopeDispose, getCurrentScope } from "vue";
/**
 * Generic polling composable using setTimeout chaining to avoid overlapping requests.
 *
 * @param pollFn - Async function called on each tick. Return `true` to stop (done), `false` to continue.
 *   The pollFn should catch its own known errors and return `true` after handling them.
 *   If pollFn throws, the composable stops with reason `"error"`.
 * @param options - Polling configuration.
 */
export const usePolling = (pollFn, options = {}) => {
    const { intervalMs = 5000, maxAttempts = 60, initialDelayMs = 0, onStop } = options;
    const isPolling = ref(false);
    let timer = null;
    let attempts = 0;
    const clearTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };
    const stopWith = (reason) => {
        clearTimer();
        isPolling.value = false;
        attempts = 0;
        onStop?.(reason);
    };
    const tick = async () => {
        if (!isPolling.value)
            return;
        attempts++;
        if (attempts > maxAttempts) {
            stopWith("max-attempts");
            return;
        }
        try {
            const done = await pollFn();
            if (done) {
                stopWith("complete");
                return;
            }
        }
        catch (err) {
            console.error("[usePolling] Unexpected error in pollFn:", err);
            stopWith("error");
            return;
        }
        // Schedule next tick only if still polling (pollFn or stop() may have changed state)
        if (isPolling.value) {
            timer = setTimeout(tick, intervalMs);
        }
    };
    const start = () => {
        if (isPolling.value)
            return;
        isPolling.value = true;
        attempts = 0;
        clearTimer();
        timer = setTimeout(tick, initialDelayMs);
    };
    const stop = () => {
        if (!isPolling.value)
            return;
        stopWith("manual");
    };
    // Safety net: clean up if the enclosing scope is disposed (component unmount or store.$dispose())
    if (getCurrentScope()) {
        onScopeDispose(() => {
            clearTimer();
            isPolling.value = false;
        });
    }
    return { isPolling: readonly(isPolling), start, stop };
};
